import { toMultilingualArrayValue } from "../../types-converter/multilingualConverter";

export type TransformResult<T> = {
  transformed: T;
  converted: number;
  convertedPaths: string[];
};

/**
 * Language keys we consider for legacy (untyped) multilingual shapes.
 * Adjust if you have more locales (e.g. "ja", "zh", etc.).
 */
const LANGUAGE_KEYS = new Set(["en", "de", "es", "fr", "it", "pt_br"]);

/**
 * True if the node is already in an "array multilingual" format.
 * We skip these to keep the transform idempotent.
 */
function isAlreadyMultilingualArrayType(node: any): boolean {
  return (
    node &&
    typeof node === "object" &&
    typeof node._type === "string" &&
    node._type.startsWith("multilingualArray")
  );
}

/**
 * Detects explicit multilingual object types (typed shapes).
 */
function isTypedMultilingualObject(node: any): boolean {
  return (
    node &&
    typeof node === "object" &&
    (node._type === "multilingualString" ||
      node._type === "multilingualText" ||
      node._type === "multilingualBlockContent")
  );
}

/**
 * Detects legacy/untyped multilingualString-like objects:
 * { en: "hello", de: "hallo", ... }
 */
function looksLikeLegacyMultilingualString(node: any): boolean {
  if (!node || typeof node !== "object") return false;

  const keys = Object.keys(node);
  const langKeys = keys.filter((k) => LANGUAGE_KEYS.has(k));
  if (langKeys.length === 0) return false;

  // At least one language key is a string
  return langKeys.some((k) => typeof (node as any)[k] === "string");
}

/**
 * Detects legacy/untyped multilingualBlockContent-like objects:
 * { en: [{_type:"block",...}], de: [...] }
 */
function looksLikeLegacyMultilingualBlockContent(node: any): boolean {
  if (!node || typeof node !== "object") return false;

  const keys = Object.keys(node);
  const langKeys = keys.filter((k) => LANGUAGE_KEYS.has(k));
  if (langKeys.length === 0) return false;

  return langKeys.some((k) => {
    const v = (node as any)[k];
    return (
      Array.isArray(v) &&
      v.some((b) => b && typeof b === "object" && b._type === "block")
    );
  });
}

/**
 * We consider "convertible" any node that:
 * - is a typed multilingual object, OR
 * - is a legacy multilingual map (string or blockcontent), AND
 * - is not already multilingualArray*
 */
function isConvertibleMultilingualNode(node: any): boolean {
  if (!node || typeof node !== "object") return false;
  if (isAlreadyMultilingualArrayType(node)) return false;
  if (node._type === "multilingualUrl") return false;

  return (
    isTypedMultilingualObject(node) ||
    looksLikeLegacyMultilingualString(node) ||
    looksLikeLegacyMultilingualBlockContent(node)
  );
}

/**
 * Normalizes a concrete path like "sections[0].items[3].title"
 * into a type-level pattern:         "sections[].items[].title"
 */
function normalizePath(path: string): string {
  return path.replace(/\[\d+\]/g, "[]");
}

/**
 * transformAll:
 * - deep traversal of the whole JSON
 * - converts ANY multilingual* node to multilingualArray* using the converter
 * - counts conversions
 * - collects normalized paths where conversions happened
 */
export function transformAll<T = any>(
  doc: T,
  options?: { skipConversionPaths?: Set<string> }
): TransformResult<T> {
  const skipPaths = options?.skipConversionPaths ?? new Set<string>();
  let convertedCount = 0;
  const rawPaths: string[] = [];

  function deepTransform(value: any, path: string): any {
    if (Array.isArray(value)) {
      return value.map((item, i) => deepTransform(item, `${path}[${i}]`));
    }

    if (value && typeof value === "object") {
      // First: rebuild children immutably and log relative path
      const rebuilt: any = {};
      for (const key of Object.keys(value)) {
        const childPath = path ? `${path}.${key}` : key;
        rebuilt[key] = deepTransform(value[key], childPath);
      }

      // Skip conversion if the path is explicitly excluded
      if (skipPaths.has(normalizePath(path))) {
        return rebuilt;
      }

      // Then: if the current node itself is a multilingual node, convert it
      if (isConvertibleMultilingualNode(rebuilt)) {
        const converted = toMultilingualArrayValue(rebuilt);

        // Count only if the conversion actually produced a multilingualArray*
        if (
          converted &&
          typeof converted === "object" &&
          typeof (converted as any)._type === "string" &&
          (converted as any)._type.startsWith("multilingualArray")
        ) {
          convertedCount++;
          rawPaths.push(path);
          return converted;
        }
      }

      return rebuilt;
    }

    return value;
  }

  const transformed = deepTransform(doc, "");
  const convertedPaths = [...new Set(rawPaths.map(normalizePath))].sort();

  return { transformed, converted: convertedCount, convertedPaths };
}
